'use client';

import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTheme, useMediaQuery } from '@mui/material';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import Footer from 'examples/Footer';
import { toast } from 'sonner';
import PostingManager from '../../components/PostingManager';
import { apiDeletePostedContent } from '../../api/posted-content.api';

const PostingManagement: React.FC = () => {
  const [searchParams] = useSearchParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  /**
   * 사용자 정보에서 branchId를 가져오는 함수
   */
  const getBranchIdFromUser = () => {
    try {
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        const user = JSON.parse(storedUser);
        console.log('📍 저장된 사용자 정보:', user);
        // user.branchInfo.id 또는 user.branch_id를 branchId로 사용
        const branchId = user.branchInfo?.id || user.branch_id;
        console.log('📍 브랜치 ID 추출:', { 
          userId: user.id, 
          branchInfoId: user.branchInfo?.id, 
          branchId: user.branch_id,
          finalBranchId: branchId 
        });
        return branchId;
      }
    } catch (error) {
      console.error('사용자 정보 파싱 오류:', error);
    }
    return null;
  };

  const urlBranchId = Number(searchParams.get('branchId'));
  const userBranchId = getBranchIdFromUser();
  const branchId = urlBranchId || userBranchId;

  // 상태 관리
  const [platformFilter, setPlatformFilter] = useState<
    'all' | 'Facebook' | 'Instagram' | 'Threads'
  >('all');
  const [postedContents, setPostedContents] = useState<any[]>([]);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    if (!branchId || branchId === 0) {
      toast.error('올바르지 않은 접근입니다. 로그인이 필요합니다.');
      return;
    }

    fetchPostedContents();
  }, [branchId]);

  /**
   * 포스팅 완료 데이터 조회
   */
  const fetchPostedContents = async (platform: string = '') => {
    if (!branchId) return;

    try {
      console.log('📊 포스팅 조회 시작:', { branchId, platform });

      const { apiGetPostedContents } = await import(
        '../../api/posted-content.api'
      );
      const postedContents = await apiGetPostedContents({
        branchId,
        platform: platform || undefined,
      });

      const formattedPostedContents = postedContents.map((content) => ({
        id: content.id,
        reviewId: content.reviewId,
        platform: content.platform,
        postId: content.postId,
        content: content.content,
        images: content.images || [],
        hashtags: content.hashtags || [],
        postedAt: content.postedAt,
        postUrl: content.postUrl,
        likes: content.likes || 0,
        comments: content.comments || 0,
        shares: content.shares || 0,
      }));

      // 날짜순 정렬 (최신순)
      formattedPostedContents.sort(
        (a, b) =>
          new Date(b.postedAt).getTime() - new Date(a.postedAt).getTime(),
      );

      setPostedContents(formattedPostedContents);
      console.log(`✅ 포스팅 조회 완료:`, formattedPostedContents.length, '개');
    } catch (error) {
      console.error('❌ 포스팅 조회 오류:', error);
      setPostedContents([]);
      toast.error('포스팅 데이터를 불러오는데 실패했습니다.');
    }
  };

  /**
   * 포스팅 수정 핸들러
   */
  const handleEditPost = () => {
    toast.info('포스팅 수정 기능은 준비 중입니다.');
  };

  /**
   * 포스팅 동기화 핸들러
   */
  const handleSyncPosts = async () => {
    if (!branchId) return;

    setSyncing(true);
    try {
      console.log('🔄 SNS 포스팅 동기화 시작:', { branchId });
      toast.info('🔄 SNS에서 포스팅을 가져오고 있습니다...');

      const { apiSyncPostsFromSocialMedia } = await import(
        '../../api/posted-content.api'
      );
      const result = await apiSyncPostsFromSocialMedia(branchId);

      if (result.success) {
        toast.success(
          `✅ ${result.syncedCount + result.reactivatedCount}개의 포스팅이 동기화되었습니다!`,
        );
        await fetchPostedContents(
          platformFilter === 'all' ? '' : platformFilter,
        );
      } else {
        toast.error(`❌ ${result.message || '포스팅 동기화에 실패했습니다.'}`);
      }
    } catch (error: any) {
      console.error('❌ 포스팅 동기화 오류:', error);
      toast.error(
        `❌ 동기화 중 오류가 발생했습니다: ${error.message || '네트워크 오류'}`,
      );
    } finally {
      setSyncing(false);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <div className={`py-4 ${isMobile ? 'px-2' : 'px-6'}`}>
        {/* 포스팅 관리자 컴포넌트 */}
        <PostingManager
          postedContents={postedContents}
          onRefresh={() =>
            fetchPostedContents(platformFilter === 'all' ? '' : platformFilter)
          }
          onEditPost={handleEditPost}
          onDeletePost={async (postId: string, platform: string) => {
            try {
              await apiDeletePostedContent(Number(postId), true);
              await fetchPostedContents(
                platformFilter === 'all' ? '' : platformFilter,
              );
            } catch (error) {
              throw error;
            }
          }}
          onSyncPosts={handleSyncPosts}
          platformFilter={platformFilter}
          onPlatformFilterChange={(platform) => {
            setPlatformFilter(
              platform as 'all' | 'Facebook' | 'Instagram' | 'Threads',
            );
            fetchPostedContents(platform === 'all' ? '' : platform);
          }}
        />
      </div>
      <Footer />
    </DashboardLayout>
  );
};

export default PostingManagement;
